import "./index.less";
import React, {
  CSSProperties,
  ReactElement,
  ReactNode,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import useScreenDesign, { screenType } from "@/Core/utils/hooks/useScreenDesign";
import { ChevronDownIcon, CloseIcon } from "../Icon";
import Portal from "../Portal";
import { withShellSafePopupContainer } from "../../layout/components/mobileCenteredShell/portal";

export type SelectValue = any;

export interface OptionInfo {
  value: SelectValue;
  label?: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  className?: string;
  _index?: number;
}

export interface SelectOptionProps {
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  label?: ReactNode;
  value: SelectValue;
}

export interface SelectTriggerProps {
  autoAlignPopupMinWidth?: boolean;
  autoAlignPopupWidth?: boolean;
  position?: "bottom" | "bl" | "br" | "top" | "tl" | "tr";
}

type PopupContainerResolver = (node?: HTMLElement | null) => Element;

export interface SelectUIProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    "defaultValue" | "onChange"
  > {
  addAfter?: ReactNode;
  addBefore?: ReactNode;
  allowClear?: boolean;
  defaultActiveFirstOption?: boolean;
  defaultValue?: SelectValue;
  disabled?: boolean;
  dropdownRender?: (menu: ReactNode) => ReactNode;
  dropdownMenuClassName?: string | string[];
  dropdownMenuStyle?: CSSProperties;
  getPopupContainer?: PopupContainerResolver;
  loading?: boolean;
  maxTagCount?: number;
  mode?: "multiple" | string;
  onChange?: (value: SelectValue, option?: OptionInfo | OptionInfo[]) => void;
  options?: SelectOptionProps[];
  placeholder?: ReactNode;
  renderFormat?: (option: OptionInfo | undefined, value: SelectValue) => ReactNode;
  showSearch?: boolean;
  size?: "mini" | "small" | "default" | "large";
  status?: "error" | "warning";
  triggerProps?: SelectTriggerProps;
  value?: SelectValue;
}

export const SelectOption = (_props: SelectOptionProps) => null;
SelectOption.displayName = "SelectOption";

const POPUP_OFFSET = 4;

const hasOwn = (object: object, key: string) =>
  Object.prototype.hasOwnProperty.call(object, key);

const toOptionInfo = (
  option: SelectOptionProps,
  index: number
): OptionInfo => ({
  value: option.value,
  label: option.label,
  children: option.children,
  disabled: option.disabled,
  className: option.className,
  _index: index,
});

const getOptionText = (option?: OptionInfo) => {
  if (!option) {
    return undefined;
  }

  return option.label ?? option.children ?? option.value;
};

const getOptionsFromChildren = (children: ReactNode): SelectOptionProps[] =>
  React.Children.toArray(children).reduce<SelectOptionProps[]>((list, child) => {
    if (!isValidElement(child)) {
      return list;
    }

    const element = child as ReactElement<SelectOptionProps>;
    if (!hasOwn(element.props, "value")) {
      return list;
    }

    list.push({
      children: element.props.children,
      className: element.props.className,
      disabled: element.props.disabled,
      label: element.props.label,
      value: element.props.value,
    });

    return list;
  }, []);

const getPopupPosition = (
  triggerNode: HTMLElement,
  position: SelectTriggerProps["position"] = "bl"
): CSSProperties => {
  const rect = triggerNode.getBoundingClientRect();

  switch (position) {
    case "top":
      return {
        left: rect.left + rect.width / 2,
        top: rect.top - POPUP_OFFSET,
        transform: "translate(-50%, -100%)",
      };
    case "tl":
      return {
        left: rect.left,
        top: rect.top - POPUP_OFFSET,
        transform: "translateY(-100%)",
      };
    case "tr":
      return {
        left: rect.right,
        top: rect.top - POPUP_OFFSET,
        transform: "translate(-100%, -100%)",
      };
    case "br":
      return {
        left: rect.right,
        top: rect.bottom + POPUP_OFFSET,
        transform: "translateX(-100%)",
      };
    case "bottom":
      return {
        left: rect.left + rect.width / 2,
        top: rect.bottom + POPUP_OFFSET,
        transform: "translateX(-50%)",
      };
    case "bl":
    default:
      return {
        left: rect.left,
        top: rect.bottom + POPUP_OFFSET,
      };
  }
};

const SelectUI = (props: SelectUIProps) => {
  const {
    addAfter,
    addBefore,
    allowClear = false,
    children,
    className,
    defaultActiveFirstOption: _defaultActiveFirstOption,
    defaultValue,
    disabled = false,
    dropdownRender,
    dropdownMenuClassName,
    dropdownMenuStyle,
    getPopupContainer,
    loading = false,
    maxTagCount: _maxTagCount,
    mode,
    onBlur,
    onChange,
    onClick,
    onFocus,
    options,
    placeholder,
    renderFormat,
    showSearch: _showSearch,
    size,
    status,
    style,
    triggerProps,
    value,
    ...restProps
  } = props;
  const mobile = useScreenDesign(screenType.mobile);
  void _defaultActiveFirstOption;
  void _maxTagCount;
  void _showSearch;
  const resolvedSize = size ?? (mobile ? "small" : "default");
  const controlled = hasOwn(props, "value");
  const isMultiple = mode === "multiple";
  const [innerValue, setInnerValue] = useState<SelectValue>(
    defaultValue ?? (isMultiple ? [] : undefined)
  );
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const [popupPosition, setPopupPosition] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const selectedValue = controlled ? value : innerValue;
  const optionList = useMemo(
    () => options || getOptionsFromChildren(children),
    [children, options]
  );
  const optionInfos = useMemo(
    () => optionList.map((option, index) => toOptionInfo(option, index)),
    [optionList]
  );
  const selectedValues = Array.isArray(selectedValue) ? selectedValue : [];
  const selectedOption = optionInfos.find((option) =>
    isMultiple
      ? selectedValues.some((valueItem) => Object.is(valueItem, option.value))
      : Object.is(option.value, selectedValue)
  );
  const popupContainerResolver = useMemo(
    () => withShellSafePopupContainer(getPopupContainer),
    [getPopupContainer]
  );
  const hasValue =
    isMultiple
      ? selectedValues.length > 0
      : selectedValue !== undefined &&
        selectedValue !== null &&
        selectedValue !== "";
  const selectedContent =
    hasValue && renderFormat
      ? renderFormat(selectedOption, selectedValue)
      : isMultiple
        ? optionInfos
            .filter((option) =>
              selectedValues.some((valueItem) => Object.is(valueItem, option.value))
            )
            .map((option) => getOptionText(option))
            .join(", ")
        : getOptionText(selectedOption);
  const visibleContent =
    selectedContent !== undefined && selectedContent !== null
      ? selectedContent
      : selectedValue;
  const adornment = addBefore || addAfter;

  const setSelectValue = useCallback(
    (nextValue: SelectValue, option?: OptionInfo) => {
      if (!controlled) {
        setInnerValue(nextValue);
      }

      onChange?.(nextValue, option);
    },
    [controlled, onChange]
  );

  const closePopup = useCallback(() => {
    setVisible(false);
  }, []);

  const togglePopup = useCallback(() => {
    if (!disabled) {
      setVisible((nextVisible) => !nextVisible);
    }
  }, [disabled]);

  useLayoutEffect(() => {
    if (!visible || !triggerRef.current) {
      return;
    }

    setPopupPosition(getPopupPosition(triggerRef.current, triggerProps?.position));
  }, [triggerProps?.position, visible]);

  useEffect(() => {
    if (!visible) {
      return undefined;
    }

    const handlePointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (
        triggerRef.current?.contains(target) ||
        popupRef.current?.contains(target)
      ) {
        return;
      }

      closePopup();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePopup();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePopup, visible]);

  const rootClassName = classNames(
    "select-ui",
    `select-ui-size-${resolvedSize}`,
    {
      "select-ui-add-after": addAfter && !addBefore,
      "select-ui-disabled": disabled,
      "select-ui-focused": focused || visible,
      "select-ui-error": status === "error",
      "select-ui-warning": status === "warning",
      "select-ui-open": visible,
    },
    className
  );
  const popupClassName = classNames(
    "select-ui-popup",
    {
      "select-ui-popup-add-after": addAfter && !addBefore,
    },
    dropdownMenuClassName
  );
  const popupStyle: CSSProperties = {
    minWidth:
      triggerProps?.autoAlignPopupWidth === false
        ? undefined
        : triggerRef.current?.offsetWidth,
    ...popupPosition,
    ...dropdownMenuStyle,
  };

  return (
    <div
      {...restProps}
      className={rootClassName}
      data-disabled={disabled ? "true" : undefined}
      data-loading={loading ? "true" : undefined}
      data-mode={mode || undefined}
      style={style}
    >
      {adornment && (
        <span className="select-ui-add-before">
          {addBefore ? addBefore : addAfter}
        </span>
      )}
      <button
        ref={triggerRef}
        aria-disabled={disabled}
        aria-expanded={visible}
        className="select-ui-trigger"
        disabled={disabled}
        type="button"
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event as any);
        }}
        onClick={(event) => {
          onClick?.(event as any);
          togglePopup();
        }}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event as any);
        }}
      >
        <span
          className={classNames("select-ui-value", {
            "select-ui-placeholder": !hasValue,
          })}
        >
          {hasValue ? visibleContent : placeholder}
        </span>
        {allowClear && hasValue && !disabled && (
          <span
            aria-label="Clear"
            className="select-ui-clear"
            role="button"
            tabIndex={-1}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              setSelectValue(isMultiple ? [] : undefined, undefined);
              closePopup();
            }}
          >
            <CloseIcon />
          </span>
        )}
        <span className="select-ui-arrow-icon" aria-hidden="true">
          <ChevronDownIcon />
        </span>
      </button>
      <Portal
        visible={visible}
        getContainer={() => popupContainerResolver(triggerRef.current)}
      >
        {visible && (
          <div ref={popupRef} className={popupClassName} style={popupStyle}>
            {dropdownRender ? (
              dropdownRender(
                <SelectOptions
                  isMultiple={isMultiple}
                  optionInfos={optionInfos}
                  selectedValue={selectedValue}
                  selectedValues={selectedValues}
                  setSelectValue={setSelectValue}
                  closePopup={closePopup}
                />
              )
            ) : (
              <SelectOptions
                isMultiple={isMultiple}
                optionInfos={optionInfos}
                selectedValue={selectedValue}
                selectedValues={selectedValues}
                setSelectValue={setSelectValue}
                closePopup={closePopup}
              />
            )}
          </div>
        )}
      </Portal>
    </div>
  );
};

const SelectOptions = ({
  closePopup,
  isMultiple,
  optionInfos,
  selectedValue,
  selectedValues,
  setSelectValue,
}: {
  closePopup: () => void;
  isMultiple: boolean;
  optionInfos: OptionInfo[];
  selectedValue: SelectValue;
  selectedValues: SelectValue[];
  setSelectValue: (value: SelectValue, option?: OptionInfo) => void;
}) => (
  <div className="select-ui-options" role="listbox">
    {optionInfos.map((option) => {
      const optionSelected = isMultiple
        ? selectedValues.some((valueItem) => Object.is(valueItem, option.value))
        : Object.is(option.value, selectedValue);

      return (
        <button
          key={`${String(option.value)}-${option._index}`}
          className={classNames("select-ui-option", option.className, {
            "select-ui-option-disabled": option.disabled,
            "select-ui-option-selected": optionSelected,
          })}
          data-value={String(option.value)}
          disabled={option.disabled}
          role="option"
          type="button"
          aria-selected={optionSelected}
          onClick={() => {
            if (option.disabled) {
              return;
            }

            if (isMultiple) {
              const nextValue = optionSelected
                ? selectedValues.filter(
                    (valueItem) => !Object.is(valueItem, option.value)
                  )
                : [...selectedValues, option.value];
              setSelectValue(nextValue, option);
              return;
            }

            setSelectValue(option.value, option);
            closePopup();
          }}
        >
          <span className="select-ui-option-content">
            {getOptionText(option)}
          </span>
        </button>
      );
    })}
  </div>
);

export default SelectUI;
